<template>
  <div class="invoice-add-wrap gk-input-light--delete">
    <portal to="dash-modal">
      <ImportToInvoiceModal
        v-if="showImport"
        :invoice="invoice"
        :is-quotation="isQuotation"
        @close="closeImportModal"
        @add-lines="addLines"
      />
      
      <ImportKvkModal
        v-if="showImportKvkModal"
        :target-data="targetData"
        @close-modal="showImportKvkModal = false"
      />

      <BrandModal 
        v-if="storeSelectedBrand"
        @close="closeBrandModal()"
        @edited="onBrandUpdated"
        @deleted="onBrandDeleted"
        @created="onBrandCreated"
      />

      <InvoiceProjectModal 
        v-if="showAddProjectModal && selectedCustomer"
        :customer-id="selectedCustomer.id"
        :project-id="(selectedProject) ? selectedProject.id: null"
        :create-project-name="newProject"
        force-open
        @close="showAddProjectModal = false"
        @cancel="closeProjectModal"
        @project-created="onProjectCreated"
        @project-updated="onProjectUpdated"
      />

      <ContactModalSimple 
        v-if="showContactModal"
        :new-customer="newCustomer"
        :contact="selectedCustomer"
        @close-modal="showContactModal = false"
        @created="prepareUpdatedCustomer"
        @updated="prepareUpdatedCustomer"
        @deleted="resetCustomer"
      />

      <InvoiceDetailsModal 
        :is-quotation="isQuotation"
        v-if="invoice && showInvoiceDetailsModal"
        :invoice="invoice"
        :preselected-project="preselectedProject"
        :selected-customer="selectedCustomer"
        @select-project="selectProject"
        @add-project-modal="(projectName) => { newProject = projectName; showAddProjectModal = true }"
        @edit-project-modal="showAddProjectModal = true"
        @close="showInvoiceDetailsModal = false"
      />
    </portal>
              
    <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">
      <div class="invoice-wrap-bg">
        <LoadingSpinner v-if="loading" />
        <div v-if="!loading" class="invoice-wrap">

          <div class="invoice-section step-1">
            <div class="step-1--customer-wrap">

              <div class="step-1--sender-wrap">
                <div class="invoice-section--label">{{ $gettext('Company profile') }}</div>

                <!-- Company info -->
                <div class="step-1--sender is-shadow-box is-shadow-box--hover" style="flex: 1; flex-direction: column;" :class="{ 'has-brand': selectedBrand }">
                  <div  style="display: flex; flex-direction: column; gap: 1rem; padding: 1rem; min-height:100px;">
                    <div style="display: flex; gap: 1rem; flex: 1;">
                      
                      <!-- Main company - not a brand -->
                      <div v-if="!selectedBrand || !currentCompany.is_premium" class="step-1--sender__info">
                        <div v-if="currentCompany.name" class="current-company-name">
                          <h3 class="cursor-pointer m-0" @click.prevent="openEditSenderModal()">
                            {{ currentCompany.name }}
                          </h3>
                        </div>
                        <div @click.prevent="openEditSenderModal()" class="cursor-pointer">
                          <div v-if="currentCompany.address">{{ currentCompany.address }}</div>
                          <div v-if="currentCompany.postal_code || currentCompany.city">
                            {{ (currentCompany.postal_code) ? currentCompany.postal_code : '' }} 
                            {{ (currentCompany.city) ? currentCompany.city : '' }}
                            {{ countries[currentCompany.country] }}
                          </div>
                        </div>
                      </div>

                      <!-- Brand selected -->
                      <div v-if="selectedBrand && currentCompany.is_premium" class="step-1--sender__info cursor-pointer" @click.prevent="openEditSenderModal()">
                        <div v-if="selectedBrand.name" class="current-company-name">
                          <h3 class="m-0">
                            {{ selectedBrand.name }}
                          </h3>
                        </div>
                        <div v-if="selectedBrand.brand_address">{{ selectedBrand.brand_address }}</div>
                        <div v-if="selectedBrand.brand_postal_code || selectedBrand.brand_city">
                          {{ (selectedBrand.brand_postal_code) ? selectedBrand.brand_postal_code : '' }} 
                          {{ (selectedBrand.brand_city) ? selectedBrand.brand_city : '' }}
                          {{ countries[selectedBrand.brand_country] }}
                        </div>
                      </div>

                      <!-- Main company logo -->
                      <div v-if="(!selectedBrand || !currentCompany.is_premium) && currentCompany.logo" class="step-1--sender__logo cursor-pointer" @click.prevent="openEditSenderModal()">
                        <img v-if="currentCompany.logo" :src="currentCompany.logo_thumbnail" :alt="currentCompany.name">
                        <div v-else class="no-logo">
                          <div>
                            <font-awesome-icon :icon="'fa-regular fa-file-circle-plus'" /><br>
                            {{ $gettext('No company logo') }}<br>
                            <span class="click-to-add">{{ $gettext('Click to add') }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- Brand logo -->
                      <div v-if="selectedBrand && selectedBrand.brand_logo && currentCompany.is_premium" class="step-1--sender__logo cursor-pointer" @click.prevent="openEditSenderModal()">
                        <img v-if="selectedBrand.brand_logo" :src="selectedBrand.brand_logo" :alt="selectedBrand.name">
                        <div v-else class="no-logo">
                          <div>
                            {{ $gettext('No brand logo') }}<br>
                            <span class="click-to-add">{{ $gettext('Click to add') }}</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                  <div v-if="!showMissingCompanyDataWarning" class="step-1-switch">
                    <div class="brand-selector">
                      <BrandSelector 
                        :preselect="selectedBrand" 
                        :hideOnSelect="false"
                        @change="selectBrand" 
                        @add-brand="initNewBrand()"
                      />
                    </div>
                    
                  </div>
                  
                </div>

              </div><!-- end step-1--sender-wrap -->
              
              <div class="step-1--recipient-wrap">
                <div class="invoice-section--label">{{ $gettext('Customer') }}</div>
                <div class="step-1--customer" :class="{ 'is-shadow-box is-shadow-box--hover': selectedCustomer }">
                  <LoadingSpinner v-if="loadingCustomer" />
                  <div v-else-if="!selectedCustomer" class="step-1--customer__selector" :class="{ 'unselected': !selectedCustomer }">
                    <CustomerSelect 
                      :preselect="preselectedCustomer"
                      :exclude="[currentCompany.id]"
                      allow-add allow-add-btn searchable 
                      :size="'lg'"
                      :toggle-variant="'primary'"
                      @selected-customer="selectCustomerAndProject"
                      @pre-selected-customer="(selected) => { selectCustomerAndProject(selected, true) }"
                      @add-customer="(customerName) => { newCustomer = customerName; showContactModal = true }"
                    />
                  </div>
                  <div v-else class="step-1--customer__customer">
                    <div class="cursor-pointer" style="display: flex; flex-direction: column; flex: 1;" @click.prevent="showContactModal = true">
                      <div v-if="selectedCustomer.name">
                        <h3 class="m-0" >
                          {{ selectedCustomer.name }}
                        </h3>
                      </div>
                      <div>
                        <div v-if="selectedCustomer.address">{{ selectedCustomer.address }}</div>
                        <div v-if="selectedCustomer.postal_code || selectedCustomer.city">
                          {{ (selectedCustomer.postal_code) ? selectedCustomer.postal_code : '' }} 
                          {{ (selectedCustomer.city) ? selectedCustomer.city : '' }}
                          {{ countries[selectedCustomer.country] }}
                        </div>
                      </div>
                    </div>
                    
                    <div class="step-1-switch">
                      <GekkoButton
                        v-if="!isEdit"
                        :text="$gettext('Switch customer')"
                        icon="fa-solid fa-arrows-rotate"
                        variant="link"
                        class="switch-btn"
                        size="sm"
                        v-gk-confirm="{
                          title: $gettext('Change customer'),
                          message: $gettext('Selecting a different customer will reset the invoice lines'),
                          options: {
                            variant: 'primary',
                            confirmText: $gettext('Ok')
                          },
                          callback: resetCustomer
                        }"
                      />
                    </div>
                    
                  </div>
                </div>
              </div>
            </div><!-- end step-1--customer-wrap -->
          </div>

          <!-- missing info warning -->
          <div v-if="showMissingCompanyDataWarning || showMissingVATCustomer" class="invoice-section step-1" style="margin-top: -1rem;">
            <div class="step-1--customer-wrap">
              <div class="step-1--sender-wrap">
                <b-alert v-if="showMissingCompanyDataWarning" show class="gk-alert mb-0" variant="warning">
                  <div @click.prevent="showImportKvkModal = true" v-html="missingDataWarningText"/>
                </b-alert>
              </div>
              <div class="step-1--sender-wrap">
                <b-alert v-if="showMissingVATCustomer" show class="gk-alert mb-0" variant="warning">
                  <div @click.prevent="showContactModal = true" v-html="vatWarningText"/>
                </b-alert>
              </div>
            </div>
          </div>          

          <!-- invoice details -->
          <div v-if="selectedCustomer && !loadingCustomer" class="invoice-section step-1">
            <div class="step-1--details-wrap">
              <div v-if="isQuotation" class="invoice-section--label">{{ $gettext('Quotation details') }}</div>
              <div v-else class="invoice-section--label">{{ $gettext('Details') }}</div>
              <div class="step-1--options-wrap is-shadow-box is-shadow-box--hover">
                
                <div class="step-1--options cursor-pointer position-relative" @click="showInvoiceDetailsModal = true">
                  <!-- Invoice Number -->
                  <div class="step-1--options__option">
                    <div v-if="isQuotation" class="invoice-section--label">{{ $gettext('Quotation Number') }}</div>
                    <div v-else class="invoice-section--label">{{ $gettext('Invoice Number') }}</div>
                    {{ invoice.number }}
                  </div>
                  <!-- Invoice date -->
                  <div class="step-1--options__option">
                    <div v-if="isQuotation" class="invoice-section--label">{{ $gettext('Quotation date') }}</div>
                    <div v-else class="invoice-section--label">{{ $gettext('Invoice date') }}</div>
                    {{ invoice.invoice_date | formatDate('DD-MM-YYYY') }}
                  </div>
                  <!-- Payment due -->
                  <div v-if="!invoice.paid_up_front" class="step-1--options__option">
                    <div v-if="isQuotation" class="invoice-section--label">{{ $gettext('Valid until') }}</div>
                    <div v-else class="invoice-section--label">{{ $gettext('Payment due') }}</div>
                    {{ invoice.due_date | formatDate('DD-MM-YYYY') }}
                  </div>
                  <!-- payment options -->
                  <div v-if="invoice.paid_up_front && invoice.invoice_payment.payment_type" class="step-1--options__option">
                    <div class="invoice-section--label">{{ $gettext('Paid by') }}</div>
                    {{ paymentTypeOptions.find(opt => opt.value === invoice.invoice_payment.payment_type).text }}
                  </div>
                  <!-- Project -->
                  <div class="step-1--options__option">
                    <div class="invoice-section--label">{{ $gettext('Project') }}</div>
                    <span v-if="invoice.project && invoice.project.name">{{ invoice.project.name }}</span>
                    <span v-else-if="invoice.project && selectedProject">{{ selectedProject.name }}</span>
                    <span v-else class="text-unassigned">{{ $gettext('none') }}</span>
                  </div>
                  <!-- Reference code -->
                  <div class="step-1--options__option">
                    <div class="invoice-section--label">{{ $gettext('Reference code') }}</div>
                    <span v-if="invoice.project_reference">{{ invoice.project_reference }}</span>
                    <span v-else class="text-unassigned">{{ $gettext('empty') }}</span>
                  </div>
                  <!-- Language -->
                  <div class="step-1--options__option">
                    <div class="invoice-section--label">
                      <div class="premium-badge--inline">
                        {{ $gettext('Language') }}
                        <GkBadge 
                          icon="fa-solid fa-crown"
                          icon-only
                          type="premium"
                        />
                      </div>
                    </div>
                    <span v-if="invoice.language">{{ langOptionsByCode(invoice.language).text }}</span>
                    <span v-else class="text-unassigned">{{ $gettext('empty') }}</span>
                  </div>
                  <!-- Currency -->
                  <div class="step-1--options__option">
                    <div class="invoice-section--label">
                      <div class="premium-badge--inline">
                        {{ $gettext('Currency') }}
                        <GkBadge 
                          icon="fa-solid fa-crown"
                          icon-only
                          type="premium"
                        />
                      </div>
                    </div>
                    <span v-if="invoice.currency">{{ invoice.currency }}</span>
                    <span v-else class="text-unassigned">{{ $gettext('empty') }}</span>
                  </div>
                </div>
                
                <div v-if="invoice.errors && invoice.errors.total_amount" class="text-danger p-3 text-center cursor-pointer position-relative" @click="showInvoiceDetailsModal = true">
                  {{ invoice.errors.total_amount }} <span class="missing-info-link">{{ $gettext('Click to edit') }}</span>
                </div>

              </div>
            </div><!-- end step-1--options-wrap -->

          </div>

          <b-alert v-if="selectedCustomer && (invoice.language !== selectedCustomer.invoice_language)" show class="gk-alert m-0" style="font-size: 1rem;" variant="warning">
            <div>
              {{ $gettext('Invoice will be translated to') }} <span class="text-uppercase">{{ invoice.language }}</span>
            </div>
          </b-alert>

          <!-- end step-1 -->

          <div v-if="selectedCustomer && !loadingCustomer" class="invoice-section">
            <div class="invoice-section--lines">
              <div v-if="isQuotation" class="invoice-section--label">{{ $gettext('Quotation lines') }}</div>
              <div v-else class="invoice-section--label">{{ $gettext('Invoice lines') }}</div>
              <div class="invoice-lines-wrap is-shadow-box">
                <InvoiceLines 
                  ref="invoice-lines"
                  v-model="invoice[invoiceLinesPropName]"
                  :invoice="invoice"
                  :lines-errors="invoiceLinesErrors"
                  importable
                  @show-import="showImport = true"
                />
                <div class="p-3">
                  <b-alert v-if="showTAXWarning" show class="gk-alert m-0" style="font-size: 1rem;" variant="warning">
                    <div v-if="showTAXWarning === 'NLTaxWarning2019Other'">
                      {{ $gettext('Inside the Netherlands, the VAT percentage is in most cases either 21% or 9%. Please make sure your current VAT is indeed correct.') }}
                    </div>
                    <div v-if="showTAXWarning === 'IntTaxWarning'">
                      {{ $gettext('When you invoice foreign entities, you usually do not use normal VAT percentages that you charge to local customers. Often you reverse charge your VAT to the foreign entity. Please make sure you are charging VAT correctly.') }}
                    </div>
                    <div v-if="showTAXWarning === 'ESTaxWarning'">
                      {{ $gettext('Inside Spain, the VAT percentage is in most cases either 21%, 10% or 4%. Please make sure your current VAT is indeed correct.') }}
                    </div>
                    <div v-if="showTAXWarning === 'AdministrativeAreaTaxWarning'">
                        {{ $gettext('The selected tax rate for this invoice differs from the commonly used rates in your administrative area. Please make sure your current VAT is indeed correct.') }}
                    </div>
                  </b-alert>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedCustomer && !loadingCustomer" class="invoice-section">
            <div class="invoice-section--message">
              <div v-if="isQuotation" class="invoice-section--label">{{ $gettext('Quotation message') }}</div>
              <div v-else class="invoice-section--label">{{ $gettext('Message') }}</div>
              <div class="w-100 gk-input-light--delete">
                <b-form-group id="input-group-message" label-class="gk-label-margin gk-modal-label" label-for="input-message">
                  <b-input-group>
                    <b-form-textarea v-if="isQuotation" id="input-message" v-model="invoice.message" :placeholder="$gettext('Message on your quotation with other instructions to your customer.')" rows="6" />
                    <b-form-textarea v-else id="input-message" v-model="invoice.message" :placeholder="$gettext('Message on your invoice with other instructions to your customer.')" rows="6" />
                  </b-input-group>
                </b-form-group>
              </div>
            </div>
          </div>

          <div v-if="isQuotation && selectedCustomer && !loadingCustomer" class="invoice-section">
            <div class="invoice-section--label">{{ $gettext('Documents & Terms') }}</div>

            <div>
              <div class="is-shadow-box" style="overflow: hidden;">
                <AttachedDocumentList 
                  deletable 
                  :object-id="invoice.id" 
                  :object-type="'Quotation'"
                  :document-list="quotationFiles"
                />
              </div>

              <GekkoButton 
                v-if="!showDocumentSelector"
                @click="displayDocumentSelector"
                :text="$gettext('Add another file')"
                icon="fa-solid fa-plus"
                variant="link"
                class="px-0"
              />

              <div v-if="showDocumentSelector">
                <DocumentSelector 
                  ref="document_selector"
                  show-file-uploader
                  :exclude-files="quotationFiles.map(file => file.id)"
                  @documentSelected="documentSelected"
                  @showFileUploader="displayFileUploader"
                />
              </div>

              <FileUploaderFull 
                v-if="showFileUploader"
                :document-name="$gettext('quotation') + ' ' + invoice.number"
                @document-selected="documentSelected"
              />

            </div>
          </div>

        </div> <!-- invoice-wrap -->
      </div> <!-- invoice-wrap-bg -->
    </overlay-scrollbars>
  </div>
</template>
<script>
import { mapGetters, mapMutations, mapActions } from "vuex";

import recurrentInvoices from "@/mixins/recurrent.invoices";

import InvoiceService from "@/store/services/invoice.service.js"
import QuotationService from "@/store/services/quotation.service.js"
import InvoicePaymentsService from "@/store/services/invoice.payments.service.js"
import CustomerService from "@/store/services/customer.service.js"
import ProjectService from "@/store/services/project.service.js"

import GekkoButton from "@/components/GekkoButton"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import InvoiceLines from "@/components/invoices/ui/InvoiceLines"

import CustomerSelect from "@/components/invoices/ui/CustomerSelect"
import CurrencyPicker from "@/components/CurrencyPicker"
import BrandSelector from "@/components/brands/BrandSelector"

import ImportToInvoiceModal from "@/components/invoices/ui/ImportToInvoiceModal"
import ImportKvkModal from "@/components/dashboard/ui/ImportKvkModal"
import ContactModalSimple from "@/components/contacts/ContactModalSimple"
import InvoiceProjectModal from "@/components/invoices/ui/InvoiceProjectModal"
import BrandModal from "@/components/brands/BrandModal"
import InvoiceDetailsModal from "@/components/invoices/ui/InvoiceDetailsModal"
import AttachedDocumentList from "@/components/documents/AttachedDocumentList"
import FileUploaderFull from "@/components/documents/FileUploaderFull";
import DocumentSelector from "@/components/documents/DocumentSelector";

export default {
  name: "InvoiceAdd",
  mixins: [recurrentInvoices],
  components: {
    GekkoButton,
    LoadingSpinner,
    InvoiceLines,
    ImportToInvoiceModal,
    CustomerSelect,
    CurrencyPicker,
    BrandSelector,
    ImportKvkModal,
    ContactModalSimple,
    InvoiceProjectModal,
    BrandModal,
    InvoiceDetailsModal,
    AttachedDocumentList,
    FileUploaderFull,
    DocumentSelector
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    isQuotation: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      invoice: {},
      loading: true,
      loadingCustomer: false,
      selectedCustomer: null,
      selectedProject: null,
      preselectedCustomer: null,
      preselectedProject: null,
      newCustomer: null,
      newProject: null,
      
      showImport: false,
      showDesign: false,
      showContactModal: false,
      showAddProjectModal: false,
      showCustomerSelector: true,
      showImportKvkModal: false,
      showBrandsSelector: false,
      showInvoiceDetailsModal: false,

      showTAXWarning: false,
      showingTAXWarning: false,
      paymentTypeOptions: [
        { value: '', text: this.$gettext('Select a payment type') },
        { value: 'cash', text: this.$gettext('Cash or private account') },
        { value: 'bank', text: this.$gettext('Bank transaction') }
      ],
      defaultEmptyPayment: { payment_type: 'cash', amount: 0, payment_date: '' },
      created_new_invoice: null,
      showDocumentSelector: false,
      showFileUploader: false,
      invoiceLinesErrors: [],
      savingImmediateBrand: false
    }
  },
  computed: {
    ...mapGetters({
      storeInvoice: 'invoices/getInvoice',
      invoicePayments: 'invoices/getInvoicePayments',
      currentCompany: "main/getCurrentCompany",
      currentLayout: "invoicesLayout/getCurrentLayout",
      langOptionsByCode: "main/getInvoiceLanguageOptionsByCode",
      languageOptions: "main/getInvoiceLanguageOptions",
      availableBrands: "brands/getAvailableBrands",
      hasMainBrand: "brands/hasMainBrand",
      brandById: "brands/getBrandById",
      ueCountryCodes: "main/getUeCountryCodes",
      companyTaxRates: "invoices/getCompanyTaxRates",
      quotationFiles: 'invoices/getQuotationFiles',
      storeSelectedBrand: 'brands/getSelectedBrand',
      countries: 'main/getCountryOptions',
      companyMissingFields: 'main/getCompanyMissingFields',
      targetData: 'main/getCompanyRequiredData',
    }),
    selectedBrand () {
      if (!this.invoice.brand && !this.currentCompany.is_premium) {
        if (this.hasMainBrand) {
          return this.hasMainBrand
        }
      }

      return (this.invoice.brand) ? this.brandById(this.invoice.brand) : null
    },
    companyMissingFields () {
      let missing_fields = []
      this.targetData.forEach(dataItem => {
        if (!this.currentCompany[dataItem.key]) {
          missing_fields.push(dataItem)
        }
      })
      return missing_fields
    },
    companyMissingFieldsText () {
      return this.companyMissingFields.map(obj => obj.label).join(', ')
    },
    selectedPaymentTypeText () {
      let type_text = ''
      if (this.invoice.invoice_payment.payment_type) {
        type_text = this.paymentTypeOptions.find(opt => opt.value === this.invoice.invoice_payment.payment_type).text
      }
      return type_text
    },
    showMissingCompanyDataWarning () {
      return (this.companyMissingFields.length > 0)
    },
    missingDataWarningText () {
      return this.$gettext('Your company profile is incomplete. <span class=\'missing-info-link\'>Click to edit</span>')
    },
    showMissingVATCustomer () {
      return this.invoice && this.invoice.vat_reversed_charge && this.selectedCustomer && !this.selectedCustomer.btw 
    },
    vatWarningText () {
      return this.$gettext('In order to reverse charge vat, you need a valid VAT number from your customer that you can report to the tax authorities. <span class=\'missing-info-link\'>Click to add</span>')
    }
  },
  watch: {
    'invoice.payment_term' () {
      this.calculateDueDate()
    },
    'invoice.invoice_date' () {
      this.calculateDueDate()
    }
  },
  created () {
    this.loading = true
    this.loadInvoiceLanguageOptions()
    this.setupData()

    const kvkTargetItem = { key: 'kvk', label: this.currentCompany.coc_label_short }
    this.targetData.push(kvkTargetItem)
  },
  methods: {
    ...mapMutations({ 
      setRecipient: 'invoices/SET_INVOICE_CREATE_RECIPIENT',
      setProject: 'invoices/SET_INVOICE_CREATE_PROJECT',
      setInvoicePayment: 'invoices/SET_INVOICE_PAYMENT',
      setInvoicePaidUpfront: 'invoices/SET_INVOICE_PAID_UPFRONT',
      setRecurrentInvoiceDate: 'invoicesRecurrent/SET_INVOICE_DATE',
      setStoreSelectedBrand: 'brands/SET_SELECTED_BRAND',
      setRefreshCustomerList: 'invoices/SET_REFRESH_CUSTOMER_LIST',
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    ...mapActions ({
      getNextInvoiceNumber: "invoices/getNextInvoiceNumber",
      getNextQuotationNumber: "invoices/getNextQuotationNumber",
      loadInvoice: 'invoices/loadInvoice',
      loadCompanyBrands: 'brands/loadCompanyBrands',
      saveRecurrent: 'invoicesRecurrent/saveRecurrent',
      addAttachedDocument: 'invoices/addAttachedDocument',
      initNewBrand: 'brands/initNewBrand',
      copyToBrand: 'brands/copyToBrand',
      loadInvoiceLanguageOptions: 'main/loadInvoiceLanguageOptions'
    }),
    setupData () {
      this.loading = true
      if (this.isQuotation) {
        this.invoiceLinesPropName = 'quotationline_set'
        this.setupQuotationData()
      } else {
        this.invoiceLinesPropName = 'invoice_lines'
        this.setupInvoiceData()
      }
      this.$watch('invoice.' + this.invoiceLinesPropName, (lines) => {
        this.checkLinesForWarnings(lines)
      }, { deep: true })
    },
    setupInvoiceData () {
      // EDIT
      if (this.isEdit && this.storeInvoice) {
        this.invoice = Object.assign({}, this.storeInvoice)

        this.setEditCustomerProject()
        this.setInvoicePayment(this.defaultEmptyPayment)
        if (this.invoice.paid_up_front) this.setInvoicePayments()
        this.$set(this.invoice, 'invoice_payment', this.storeInvoice.invoice_payment)
        this.loading = false
      } else {
        // CREATE
        this.setInvoiceCreateDefaults()
        this.$set(this.invoice, this.invoiceLinesPropName, [])
        if(!this.invoice.brand && this.currentCompany.is_premium){
          if (this.hasMainBrand) {
            this.invoice.brand = this.hasMainBrand.id
          }
        }

        // check for customerId
        if (this.$route.params['customerId']) {
          this.getCustomerData({ name: '', customer_id: parseInt(this.$route.params['customerId']), project_id: null }, true).then(() => {
            this.$emit('display-buttons', 'create')

            if (!this.$route.params['projectId'] && this.$route.query.import) {
              this.showImport = true
            }
          }).catch((error) => {
            // console.log('could not retrieve customer', error)
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.$gettext('There was a problem retrieving that customer'),
              variant: 'danger',
              duration: 4
            })
            this.$router.push({ name: 'Invoices' }).catch(() => {})
          })
        }

        // check for projectId
        if (this.$route.params['projectId']) {
          this.retrieveAndSetProject(parseInt(this.$route.params['projectId'])).then(() => {
            if (this.$route.query.import) {
              this.showImport = true
            }
          }).catch((error) => {
            // console.log('could not retrieve project', error)
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.$gettext('There was a problem retrieving that project'),
              variant: 'danger',
              duration: 4
            })
            this.$router.push({ name: 'Invoices' }).catch(() => {})
          })
        }

        // default payment
        this.$set(this.invoice, 'invoice_payment', this.defaultEmptyPayment)
        
        const data = { 'sender_company': this.currentCompany.id, 'year': new Date().format('Y') }
        this.getNextInvoiceNumber(data).then((response) => {
          this.$set(this.invoice, 'number', response)
          this.loading = false
        })

        this.setRecurrentInvoiceDate(this.invoice.invoice_date)
      }
    },
    setupQuotationData () {
      if (this.isEdit && this.storeInvoice) {
        this.invoice = Object.assign({}, this.storeInvoice)

        this.setEditCustomerProject()

        const payment_term = Math.floor((new Date(this.invoice.due_date) - new Date(this.invoice.invoice_date)) / (1000*60*60*24));
        this.$set(this.invoice, 'payment_term', payment_term)
        this.loading = false
      } else {
        this.setInvoiceCreateDefaults()
        if(!this.invoice.brand && this.currentCompany.is_premium){
          if(this.hasMainBrand){
            this.invoice.brand = this.hasMainBrand.id;
          }
        }
        this.$set(this.invoice, this.invoiceLinesPropName, [])

        const data = { 'sender_company': this.currentCompany.id, 'year': new Date().format('Y') }
        this.getNextQuotationNumber(data).then((response) => {
          this.$set(this.invoice, 'number', response)
          this.loading = false
        })
      }
    },
    setInvoiceCreateDefaults () {
      this.invoice = {
        number: null,
        invoice_date: new Date().format('Y-m-d'),
        payment_term: this.currentCompany.default_payment_term,
        sender_company: this.currentCompany.id,
        vat_reversed_charge: this.currentCompany.default_tax_rate_reversed,
        line_amount_is_gross: this.currentCompany.companysettings.default_invoice_inc_vat,
        recipient_customer: null,
        language: 'nl', // 'nl' as default, customer.invoice_language when selected
        currency: 'EUR',
        brand: (this.selectedBrand && this.currentCompany.is_premium) ? this.invoice.brand : null,
        paid_up_front: false,
        message: (this.isQuotation) ? '' : this.currentCompany.default_invoice_message
      }
    },

    setEditCustomerProject () {
      // select customer
      this.preselectedCustomer = { name: '', customer_id: null, project_id: null }
      if (this.invoice.recipient_customer) {
        this.preselectedCustomer.customer_id = this.invoice.recipient_customer.id
        this.preselectedCustomer.name = this.invoice.recipient_customer.name
      }
      this.preselectedProject = { name: '', project_id: null }
      if (this.invoice.project) {
        this.preselectedProject.project_id = this.invoice.project.id
        this.preselectedProject.name = this.invoice.project.name

        this.selectedProject = this.invoice.project
        this.invoice.project = this.invoice.project.id
      }
    },
    closeDesignModal () {
      this.showDesign = false
    },
    closeImportModal () {
      this.showImport = false
    },
    prepareUpdatedCustomer (customer) {
      // set customer list (on filters) to refresh when refreshing invoices table
      this.setRefreshCustomerList(true)

      const selected = {
        name: customer.name,
        customer_id: customer.id,
        project_id: null
      }
      this.selectCustomerAndProject(selected)
    },
    selectCustomerAndProject (selected, preselected = false) {
      if (selected.customer_id) {
        // get full customer info
        this.getCustomerData(selected, preselected)
        this.$emit('display-buttons', 'create')
      } else {
        this.selectedCustomer = null
        this.$emit('display-buttons', '')
      }
    },
    selectProject (selected, query = true) {
      if (selected && !query) {
        this.selectedProject = selected
        this.$set(this.invoice, 'project_reference', this.selectedProject.reference)
        this.$set(this.invoice, 'project', this.selectedProject.id)
        this.setProject(this.selectedProject)
      } else if (selected && selected.id) {
        this.retrieveAndSetProject(selected.id)
      } else {
        this.preselectedProject = { name: '', project_id: null }
        this.selectedProject = null
        this.$set(this.invoice, 'project_reference', null)
        this.$set(this.invoice, 'project', null)
        this.setProject(null)
      }
    },
    retrieveAndSetProject (project_id) {
      return ProjectService.retrieve(project_id).then((response) => {
        this.selectedProject = response.data
        this.preselectedProject = { name: '', project_id: this.selectedProject.id }
        this.$set(this.invoice, 'project_reference', this.selectedProject.reference)
        this.$set(this.invoice, 'project', this.selectedProject.id)
        this.setProject(this.selectedProject)
      })
    },
    selectBrand (brand_id) {
      this.invoice.brand = brand_id
    },
    selectCurrency (currency) {
      this.invoice.currency = currency
    },
    resetCustomer () {
      this.selectedCustomer = null
      this.selectedProject = null
      this.preselectedProject = null
      this.invoice[this.invoiceLinesPropName].splice(0)
      this.$emit('display-buttons', '')
      this.setupData()
    },
    getCustomerData (selected, preselected = false) {
      this.loadingCustomer = true
      return CustomerService.retrieve(selected.customer_id).then((response) => {
        this.selectedCustomer = response.data

        this.invoice.recipient_customer = selected.customer_id

        // if edit invoice will maintain its reversed_charge value
        if (!this.isEdit) {
          if (this.selectedCustomer.country === this.currentCompany.country) {
            this.invoice.vat_reversed_charge = this.currentCompany.default_tax_rate_reversed
          } else if (this.ueCountryCodes.includes(this.selectedCustomer.country)) {
            this.invoice.vat_reversed_charge = true
          }
        }

        this.setRecipient(this.selectedCustomer)

        // set project if selected during invoice creation
        if (selected.project_id) {
          this.retrieveAndSetProject(selected.project_id)
        }


        // set customer's payment_term (not on edit init)
        if (!preselected) {
          if (this.selectedCustomer.default_payment_term && this.selectedCustomer.default_payment_term != this.currentCompany.default_payment_term ) {
            this.invoice.payment_term = this.selectedCustomer.default_payment_term
          } else {
            this.invoice.payment_term = this.currentCompany.default_payment_term
          }
        }
        
        this.$set(this.invoice, 'language', this.selectedCustomer.invoice_language)
        this.loadingCustomer = false
      })
    },
    getTotals () {
      const totals = {
        vat_totals: [],
        subtotal: 0,
        total: 0
      }
      let vat_totals = []
      if (this.$refs['invoice-lines']) {
        vat_totals = this.$refs['invoice-lines'].vatTotals
        vat_totals = Object.entries(vat_totals).map(([key, value]) => [parseInt(key), value])
        totals.vat_totals = vat_totals
        totals.subtotal = this.$refs['invoice-lines'].subtotal
        totals.total = this.$refs['invoice-lines'].total
      }

      return totals
    },
    addLines (lines) {
      this.$refs['invoice-lines'].addLinesViaImport(lines)
    },
    calculateDueDate () {
      const due_date = Date.parseExact(this.invoice.invoice_date, 'yyyy-MM-dd')
      due_date.add({ days: this.invoice.payment_term })
      this.invoice.due_date = due_date.toString('yyyy-MM-dd')
    },
    validateLines () {
      let valid_lines = true
      if (this.invoice[this.invoiceLinesPropName].length === 1 && this.invoice[this.invoiceLinesPropName][0].default) {
        valid_lines = false
      } else {
        this.invoice[this.invoiceLinesPropName].forEach(line => {
          if (!line.default && line.amount === '') {
            valid_lines = false
          }
        })
      }
      return valid_lines
    },
    /**
     * Calls save invoice or quotation _methods
     * 
     * @param {boolean} and_send flag for saving and send (quotations only). Default is null
     */
    saveInvoice (and_send = null) {
      if (!this.validateLines()) {
        this.$store.commit('setAlert', {
          title: this.$gettext('Could not save'),
          text: this.$gettext('Please review invoice line amounts'),
          variant: 'danger',
          duration: 4
        })
        return
      }
      if (this.isQuotation) {
        this._saveQuotation(and_send)
      } else {
        this._saveInvoice()
      }
    },
    _saveInvoice () {
      if (this.invoice.currency != 'EUR' && this.invoice.paid_up_front) {
        if (!(this.invoice.total_amount > 0) && parseFloat(this.$refs['invoice-lines'].total) > 0) {
          this.editingDetails = true
          if (!this.invoice.errors) {
            this.$set(this.invoice, 'errors', {})
          }
          this.$set(this.invoice.errors, 'total_amount', this.$gettext("This invoice is in a foreign currency and marked as paid. Please provide the invoice total in euro's."))
          this.$store.commit('setAlert', {
            title: this.$gettext('Could not save'),
            text: this.invoice.errors.total_amount,
            variant: 'danger',
            duration: 4
          })
          return
        } else {
          if (this.invoice.errors && this.invoice.errors.total_amount) {
            this.$set(this.invoice.errors, 'total_amount', null)
          }
          this.invoice.invoice_payment.amount = this.invoice.total_amount
        }
      }

      this.setWaitingForResponse(true)

      // Prepare lines
      this.prepareLinesForSend()

      let saveInvoicePromise, invoiceResponse

      this.__updateInvoicePayment();

      if (this.invoice.id == null) {
        // CREATE
        saveInvoicePromise = InvoiceService.create(this.invoice).then((response) => {
          invoiceResponse = response.data
          if (this.invoice.paid_up_front) {
            this.invoice.invoice_payment.invoice = invoiceResponse.id
            return InvoicePaymentsService.create(this.invoice.invoice_payment)
          }
          return invoiceResponse
        }).catch((response) => {
          if (invoiceResponse) {
            return InvoiceService.destroy(invoiceResponse.id)
          }
          throw response
        }).finally(() => {
          this.setWaitingForResponse(false)
        })
      } else {
        // EDIT
        if (!this.currentCompany.is_premium) {
          this.invoice.brand = null
        }
        saveInvoicePromise = InvoiceService.update(this.invoice.id, this.invoice).then((response) => {
          invoiceResponse = response.data
          this.invoice.invoice_payment.invoice = invoiceResponse.id
          if (this.invoice.paid_up_front) {
            if (this.invoice.invoice_payment.id) {
              return InvoicePaymentsService.partial_update(this.invoice.invoice_payment)
            } else {
              return InvoicePaymentsService.create(this.invoice.invoice_payment)
            }
          } else {
            if (this.invoice.invoice_payment.id) {
              return InvoicePaymentsService.destroy(this.invoice.invoice_payment.id)
            }
          }
          return response
        }).then((response) => {
          return response
        }).finally(() => {
          this.setWaitingForResponse(false)
        });
      }

      saveInvoicePromise.then(() => {
        this.__successFunction(invoiceResponse)
      }, this.__failFunction)
    },
    _saveQuotation (and_send) {
      // Prepare lines
      this.prepareLinesForSend()

      let saveQuotationPromise, quotationResponse

      // set quotation documents
      this.invoice.documents = this.quotationFiles.map(file => file.id)

      // set due_date (based on payment_term)
      // this.invoice.due_date = 

      this.setWaitingForResponse(true)
      if (this.invoice.id == null) {
        // CREATE
        saveQuotationPromise = QuotationService.create(this.invoice).then((response) => {
          quotationResponse = response.data
          return response
        })
      } else {
        // EDIT
        saveQuotationPromise = QuotationService.update(this.invoice.id, this.invoice).then((response) => {
          quotationResponse = response.data
          return response
        })
      }
      saveQuotationPromise.then(() => {
        this.__successFunctionQuotation(quotationResponse, and_send)
      }, this.__failFunction).finally(() => {
        this.setWaitingForResponse(false)
      })
    },
    __updateInvoicePayment() {
      if (this.invoice.currency == 'EUR') {
        this.invoice.invoice_payment.amount = this.$refs['invoice-lines'].total
      }
      this.invoice.invoice_payment.payment_date = this.invoice.invoice_date
    },
    __successFunction(invoice) {
      this.$router.push({ name: 'InvoiceView', params: { invoiceId: invoice.id } }).catch(() => {})
      this.$emit('invoice-updated')
    },
    __successFunctionQuotation(invoice, and_send = null) {
      if (and_send) {
        this.$router.push({ name: 'QuotationSend', params: { quotationId: invoice.id } }).catch(() => {})
      } else {
        this.$router.push({ name: 'QuotationView', params: { quotationId: invoice.id } }).catch(() => {})
      }
      this.$emit('invoice-updated')
    },
    __failFunction(errors) {
      this.$refs['invoice-lines'].addNewLine()

      this.invoiceLinesErrors = (errors.data.invoice_lines) ? errors.data.invoice_lines : []

      if (errors.data.due_date) {
        this.$store.commit('setAlert', {
          title: this.$gettext('Could not save'),
          text: this.$gettext('The quotation Valid until date is in the past. Please increase the payment term.'),
          variant: 'danger',
          duration: 4
        })
      }
      if (errors.data.number) {
        let alertText = this.$gettext('The invoice number already exists.')
        if (this.isQuotation) {
          alertText = this.$gettext('The quotation number already exists.')
        }
        this.$store.commit('setAlert', {
          title: this.$gettext('Could not save'),
          text: alertText,
          variant: 'danger',
          duration: 4
        })
      }
    },
    prepareLinesForSend () {
      if (this.invoice.vat_reversed_charge) {
        this.invoice[this.invoiceLinesPropName].forEach(line => {
          line.tax_rate = 0
        })
      }
      // deregister the $watch on lines by calling it again
      this.$refs['invoice-lines'].removeLineWatch()

      // remove last empty line
      this.removeLastDefaultLine(this.invoice)
    },
    removeLastDefaultLine (invoice) {
      const invoice_lines = invoice[this.invoiceLinesPropName]
      if (invoice_lines.length > 0 && invoice_lines[invoice_lines.length - 1].default) {
        invoice_lines.splice(invoice_lines.length - 1, 1)
      }
      return invoice
    },
    setInvoicePayments () {
      if (this.invoicePayments && this.invoicePayments.length > 0 && this.invoicePayments[0].payment_type) {
        this.setInvoicePayment(this.invoicePayments[0])
      } else {
        this.setInvoicePaidUpfront(false)
      }
    },
    openEditSenderModal () {
      if (this.selectedBrand) {
        this.openBrandModal()
      } else {
        // this.showImportKvkModal = true
        if (!this.hasMainBrand) {
          this.openMainBrandModal()
        }
      }
    },
    openBrandModal () {
      this.setStoreSelectedBrand(this.selectedBrand)
    },
    closeBrandModal () {
      this.setStoreSelectedBrand(null)
    },
    onBrandCreated (brand) {
      this.selectBrand(brand.id)
      this.setStoreSelectedBrand(null)
      this.loadCompanyBrands(this.currentCompany.id)
    },
    onBrandUpdated () {
      this.setStoreSelectedBrand(null)
      this.loadCompanyBrands(this.currentCompany.id)
    },
    onBrandDeleted () {
      this.setStoreSelectedBrand(null)
      this.selectedBrand = null
      this.loadCompanyBrands(this.currentCompany.id)
    },
    closeProjectModal () {
      this.showAddProjectModal = false
    },
    onProjectCreated (payload) {
      this.newProject = null
      this.closeProjectModal()
      
      this.preselectedProject = { name: '', project_id: payload.id }

      this.selectProject({ id: payload.id })
    },
    onProjectUpdated (payload) {
      this.selectProject(payload, false)

      this.closeProjectModal()
    },
    onProjectDeleted () {
      this.closeProjectModal()
      this.selectProject(null)
    },
    checkLinesForWarnings (lines) {
      this.showTAXWarning = false

      if (lines.length === 1 && lines[0].default) {
        this.showTAXWarning = false
      } else if (this.selectedCustomer && this.selectedCustomer.country) {
        lines.forEach(line => {
          if (this.currentCompany.country === 'ES' && this.selectedCustomer.country === 'ES' && !this.currentCompany.administrative_area) {
            // check for ESTaxWarning
            if (line.tax_rate != null && ([21, 10, 4].indexOf(line.tax_rate) == -1) || line.vat_exempt || this.invoice.vat_reversed_charge) {
              this.showTAXWarning = 'ESTaxWarning'
            }
          } else if (this.currentCompany.country === 'NL' && this.selectedCustomer.country == 'NL' && this.currentCompany.administrative_area) {
            // check for NLTaxWarning2019Other
            if (line.tax_rate != null && ([21, 9].indexOf(line.tax_rate) == -1) || line.vat_exempt || this.invoice.vat_reversed_charge) {
              this.showTAXWarning = 'NLTaxWarning2019Other'
            }
          } else if (this.currentCompany.country !== this.selectedCustomer.country) {
            // check for IntTaxWarning
            if (!(line.tax_rate == null || line.tax_rate == 0 || line.vat_exempt || this.invoice.vat_reversed_charge)) {
              this.showTAXWarning = 'IntTaxWarning'
            }
          } else if (this.currentCompany.administrative_area) {
            const taxRateValues = this.companyTaxRates.map(taxRate => taxRate.taxrate)
            if (!taxRateValues.includes(line.tax_rate)) {
              this.showTAXWarning = 'AdministrativeAreaTaxWarning'
            }
          }
        })
      }
    },
    displayDocumentSelector () {
      this.showFileUploader = false
      this.showDocumentSelector = true
    },
    displayFileUploader () {
      this.showFileUploader = true
      this.showDocumentSelector = false
    },
    documentSelected (item) {
      this.showDocumentSelector = false
      this.addAttachedDocument({ type: (this.isQuotation) ? 'Quotation': 'Correction', file: item })
    },
    openMainBrandModal () {
      if (!this.savingImmediateBrand) {
        if (this.showMissingCompanyDataWarning) {
          this.showImportKvkModal = true
        } else {
          // save a new brand immediately using company data
          this.savingImmediateBrand = true
          this.copyToBrand()
        }
      }
    }
  }
}
</script>

<style lang="scss">
  .invoice-add-wrap {
    .step-1-switch {
      .brand-selector {
        width: 300px;
        text-align: right;
        .selected-brand {
          justify-content: flex-end;
        }
      }
    }
    .step-1--customer__selector {
      .toggle-primary {
        height: 100px;
        border-radius: $radiusLg;
        .dropdown-toggle {
          border-radius: $radiusLg;
        }
      }
    }
  }
</style>

<style scoped lang="scss">
  .invoice-add-wrap {
    height: 100%;
    margin: 0 auto;
    border: none !important;
    border-radius: 0 !important;
    .gk-alert {
      font-size: 14px; 
      padding: 0.5rem;
    }
    .invoice-section {
      &.step-1 {
        padding: 0rem;
        gap: 0;
        display: flex;
        justify-content: center;

        .step-1--sender-wrap,
        .step-1--recipient-wrap {
          width: 100%;
          @media (min-width: '768px') { 
            width: 50%;
          }
        }
        .step-1--customer-wrap,
        .step-1--details-wrap {
          @media (min-width: '992px') { 
            width: 100%;
          }
        }
        .step-1--sender-wrap,
        .step-1--recipient-wrap {
          display: flex;
          flex-direction: column;
        }
        .step-1--recipient-wrap {
          .customer-selector-wrap {
            max-width: 320px;
            text-align: right;
          }
        }
        .step-1--customer-wrap {
          display: flex;
          font-size: 16px;
          gap: 1rem;
          flex-direction: column;
          @media (min-width: '576px') {
            flex-direction: row;
          }
        }
        .step-1--sender {
          display: flex;
          position: relative;
          &__info {
            flex: 1;
            font-size: 14px;
            .current-company-name {
              .brands-company {
                font-size: 14px;
                opacity: 0.5;
              }
            }
            .field-label {
              font-family: DINNextLTPro-Medium, sans-serif;
            }
          }
          &__logo {
            width: 55px;
            height: 55px;
            position: relative;
            overflow: hidden;
            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              object-position: top;
            }
            .no-logo {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 100%;
              background: $inputBackgroundColor;
              text-align: center;
              font-size: 14px;
              .svg-inline--fa {
                color: $colorTextSemi;
                opacity: 0.3;
                font-size: 22px;
              }
              .click-to-add {
                color: $colorTextSemi;
              }
            }
          }
          .invoice-brand-select-wrap {
            padding: 0.5rem;
            padding-top: 0;
            &.show-selector {
              padding: 0.5rem;
            }
            .invoice-section--label {
              font-size: 14px;
            }
          }
        }
        
        .step-1--customer {
          position: relative;
          display: flex;
          flex: 1;
          &__selector,
          &__customer {
            flex: 1;
            padding: 1rem;
          }
          &__customer {
            display: flex;
            font-size: 14px;
          }
          &__selector {
            text-align: right;
            &.unselected {
              padding: 0;
            }
          }
        }

        .step-1--options-wrap {
          display: flex;
          flex-direction: column;
          @media (min-width: '992px') { 
            width: 100%;
          }
        }
        .step-1--options {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          width: 100%;
          padding: 1rem;
          .invoice-section--label {
            margin-bottom: 0;
            font-size: 14px;
            color: $colorTextBase;
          }
          @media (min-width: '576px') {
            grid-template-columns: 1fr 1fr;
          }
          @media (min-width: '768px') { 
            grid-template-columns: 1fr 1fr 1fr;
          }
          @media (min-width: '992px') { 
            grid-template-columns: 1fr 1fr 1fr 1fr;
          }
          &.option-project {
            grid-template-columns: 1fr 1fr;
            .selector-wrap {
              display: flex;
              gap: 1rem;
              .dropdown {
                max-width: 320px;
              }
            }
          }
        }
        .b-form-datepicker { height: 38px; }
        .step-1-switch {
          padding: 0;
          position: absolute;
          bottom: 0.5rem;
          right: 0.5rem;
          opacity: 1;
          z-index: 10;
          text-align: right;
          .switch-btn {
            color: $colorBlue;
            line-height: 0;
            font-size: 14px;
            border: 0;
            padding: 0;
          }
        }
      }
      &.gekkofooter { justify-content: flex-end; }
    }
    .invoice-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }
  }
</style>
